import { CommonModule, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@ngneat/transloco';
import { CUSTOMER_CONFIG } from 'app/core/customer/customer.config';
import { CustomerTopBarBranding } from 'app/core/customer/customer-config.model';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ThemeToggleComponent } from 'app/modules/shared/theme-toggle/theme-toggle.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { TradexImportStatusPanelComponent } from 'app/modules/tools-reports/tradex-catch-certificate/tradex-import-status-panel/tradex-import-status-panel.component';

@Component({
  selector: 'dense-layout',
  templateUrl: './dense.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    SearchComponent,
    UserComponent,
    RouterOutlet,
    CommonModule,
    ThemeToggleComponent,
    MatTooltipModule,
    TranslocoModule,
    TradexImportStatusPanelComponent,
  ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation = {
    compact: [],
    default: [],
    futuristic: [],
    horizontal: [],
  };
  navigationAppearance: 'default' | 'dense' = 'dense';
  readonly topBarBranding: CustomerTopBarBranding | undefined = CUSTOMER_CONFIG.topBarBranding;
  contextTitle = '';
  readonly _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    readonly _activatedRoute: ActivatedRoute,
    readonly _router: Router,
    readonly _navigationService: NavigationService,
    readonly _fuseMediaWatcherService: FuseMediaWatcherService,
    readonly _fuseNavigationService: FuseNavigationService,
  ) {}

  goToCompanySelection(): void {
    this._router.navigate(['/main']);
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.updateContextTitle(this._router.url);

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event) => this.updateContextTitle(event.urlAfterRedirects));

    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
      // Check if the screen is small
      this.isScreenSmall = !matchingAliases.includes('md');

      // Change the navigation appearance
      this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this.navigationAppearance = this.navigationAppearance === 'default' ? 'dense' : 'default';
  }

  private updateContextTitle(url: string): void {
    if (this.topBarBranding?.title) {
      this.contextTitle = this.topBarBranding.title;
      return;
    }

    if (url.startsWith('/wild-harvest')) {
      this.contextTitle = 'Wild Harvest';
    } else if (url.startsWith('/aquaculture')) {
      this.contextTitle = 'Aquaculture';
    } else {
      this.contextTitle = '';
    }
  }
}
